import { REPORT_ENDPOINT } from "./endpoints";
import { fetcher } from "./utils";
// @ts-ignore
import html2pdf from "html2pdf.js";

export type Report = {
    html: string;
    logo_base64?: string;
    status: "Created" | "Edited" | "Signed" | "Deleted";
    font_size?: string;
};

export const getReport = async ({ testId }: { testId: string }) => {
    const response = await fetcher(REPORT_ENDPOINT, {
        params: {
            test_id: testId,
        },
        method: "GET",
        headers: {
            "Content-Type": "application/json",
        },
    });

    return response as Report | null;
};

export const submitReport = async ({
    testId,
    report,
}: {
    testId: string;
    report: Pick<Report, "html" | "logo_base64">;
}) => {
    const response = await fetcher(
        REPORT_ENDPOINT,
        {
            params: {
                test_id: testId,
            },
            body: JSON.stringify({
                logo_base64: report.logo_base64,
                html: report.html,
            } as Report),
            method: "PUT",
            headers: {
                "Content-Type": "application/json",
            },
        },
        false,
        true
    );

    return response;
};

export type SignatureReason =
    | "Review"
    | "Approval"
    | "Responsibility"
    | "Authorship";

export type SignAndLockResponseFromAPI =
    | Report
    | { message: string; code: "OTP_sent" };

export const signAndLockReport = async ({
    testId,
    reason,
}: {
    testId: string;
    reason: SignatureReason;
}): Promise<SignAndLockResponseFromAPI> => {
    const response = await fetcher(
        `${REPORT_ENDPOINT}/sign-and-lock`,
        {
            params: {
                test_id: testId,
            },
            body: JSON.stringify({ reason }),
            method: "PUT",
            headers: {
                "Content-Type": "application/json",
            },
        },
        false,
        true
    );
    if (!response.ok)
        throw new Error(
            `Failed to sign and lock report: ${response.statusText}`
        );

    return response.json();
};

export const verifyReportMFA = async (code: string) => {
    const response = await fetcher(
        `${REPORT_ENDPOINT}/sign-and-lock/verify-mfa`,
        {
            body: JSON.stringify({ code }),
            method: "PUT",
            headers: {
                "Content-Type": "application/json",
            },
        },
        false,
        true
    );
    if (!response.ok) {
        const resData: { message: string } = await response.json();
        throw new Error(resData.message);
    }

    const result = await response.json();
    if (result.code !== "OTP_verified") {
        throw new Error("OTP verification failed");
    }
    return result;
};

export type ExportReportFileType = "docx" | "pdf";

export const generateDocxReport = async ({
    testId,
    html,
}: {
    testId?: string;
    html: string;
}) => {
    const response = await fetcher(
        `${REPORT_ENDPOINT}/as-docx`,
        {
            params: { test_id: testId || "" },
            method: "POST",
            data: { html },
        },
        false,
        true
    );

    if (!response || response.status !== 200) {
        console.error(`Error converting to docx: ${response?.statusText}`);
        return null;
    }

    const reader = response?.body?.getReader();
    if (!reader) {
        console.error("No reader found in response body");
        return null;
    }

    const stream = new ReadableStream({
        start(controller) {
            function push() {
                reader
                    .read()
                    .then(
                        ({
                            done,
                            value,
                        }: {
                            done: boolean;
                            value?: Uint8Array;
                        }) => {
                            if (done) {
                                controller.close();
                                return;
                            }
                            controller.enqueue(value);
                            push();
                        }
                    )
                    .catch((error: any) => {
                        console.error("Error in stream", error);
                        controller.error(error);
                    });
            }
            push();
        },
    });

    return new Response(stream);
};

export const generatePdfReport = async ({ html }: { html: string }) => {
    const style = `
        :root {
            --page-markup-color: #ababab;
            --highlighted-border-color: #366cd6;
            --highlighted-background-color: #b3d4ffaf;
        }

        body {
            margin: 0;
            padding: 0;
        }

        .page {
            .page-header {
                .header-image {
                    margin-top: -20px;
                }
            }
            
            padding-top: 0 !important;
            font-family: "Open Sans", sans-serif;
            border: none;
            height: auto !important;

            .page-break {
                page-break-before: always;
            }
            h1, h2, h3 {
                margin: 0;
            }
            
            ul {
                list-style-type: disc;
                padding-left: 2em;
                margin: 0.5em 0;
            }
            ol {
                list-style-type: decimal;
                padding-left: 2em;
                margin: 0.5em 0;
            }
            li {
                margin: 0.25em 0;
            }
            a {
                color: #0066cc;
                text-decoration: underline;
                cursor: pointer;
            }
            p {
                margin: 0;
            }

            table {
                border-collapse: collapse;
                margin: 0;
                overflow: hidden;
                table-layout: fixed;
                width: 100%;
                font-size: inherit;

                td,
                th {
                    box-sizing: border-box;
                    min-width: 1em;
                    position: relative;
                    vertical-align: middle !important;
                    height: 22px;

                    > * {
                        margin-bottom: 0;
                        line-height: 1;
                        vertical-align: middle;
                        transform: translateY(-8px);
                    }
                }

                th {
                    background-color: #f0f0f0;
                    font-weight: bold;
                    text-align: left;
                }
            }
            .image-component {
                img {
                    height: auto;
                }
            }

            [data-remove-border-on-print="true"] {
                border: none !important;
            }
        }
    `;
    const htmlWithStyle = `
        <style>
            ${style}
        </style>
        ${html
            .replace(/ contenteditable="false"/g, "")
            // increase bottom padding by 10px
            .replace(/padding-bottom:\s*(\d+)px/g, (match, p1) => {
                const newPadding = parseInt(p1) + 10;
                return `padding-bottom: ${newPadding}px`;
            })
            // decrease top padding by 10px
            .replace(/padding-top:\s*(\d+)px/g, (match, p1) => {
                const newPadding = parseInt(p1) - 10;
                return `padding-top: ${newPadding}px`;
            })}
    `;

    const element = document.createElement("div");
    element.innerHTML = htmlWithStyle;

    const options = {
        margin: 0,
        filename: "report.pdf",
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: { scale: 2 },
        jsPDF: { unit: "pt", format: "a4", orientation: "portrait" },
    };

    return html2pdf().from(element).set(options).save();
};
